import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { axiosInstance } from "../../utils/axiosInstance";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  RefreshButton,
} from "../ui";
import ReportsDateFilter from "./ReportsDateFilter";
import ReportsSubNav from "./ReportsSubNav";
import { MachinesLeftCompletePayload } from "./reportsTypes";

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}

function formatMinutes(m: number): string {
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rem = Math.round(m % 60);
    return `${h}h ${rem}m`;
  }
  return `${Math.round(m)}m`;
}

export default function MachinesLeftCompleteReport() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState<MachinesLeftCompletePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: string, t: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get<MachinesLeftCompletePayload>(
        "/reports/machines-left-complete",
        { params: { from: f, to: t } }
      );
      setData(res.data);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to load machines-left-complete report."
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyRange = (f: string, t: string) => {
    setFrom(f);
    setTo(t);
    load(f, t);
  };

  const worstLocationId = data && data.byStore.length ? data.byStore[0].locationId : null;

  return (
    <PageContainer>
      <PageHeader
        title="Machines left complete"
        subtitle={
          data
            ? `${data.range.from} to ${data.range.to} · flagged when a cycle sits finished for ${data.thresholdMinutes}+ minutes before the next status change`
            : undefined
        }
        actions={<RefreshButton onClick={() => load(from, to)} loading={loading} />}
      />

      <div className="sq-stack">
        <ReportsSubNav />
        <ReportsDateFilter from={from} to={to} onApply={applyRange} loading={loading} />

        {error ? <ErrorBanner onRetry={() => load(from, to)}>{error}</ErrorBanner> : null}
        {loading && !data ? (
          <Card>
            <LoadingPanel label="Loading report" />
          </Card>
        ) : null}

        {data ? (
          <div className="sq-columns">
            <Card
              flush
              title="Worst stores for machines left complete"
              subtitle="Ranked by average time a machine sits in COMPLETE before being reset."
              aria-label="By store"
            >
              {data.byStore.length === 0 ? (
                <EmptyState title="No COMPLETE status events in this range" />
              ) : (
                <div className="sq-table-wrap">
                  <table className="sq-table is-stacked cols-4">
                    <thead>
                      <tr>
                        <th scope="col">Store</th>
                        <th scope="col" className="num">Avg time to clear</th>
                        <th scope="col" className="num">Longest</th>
                        <th scope="col" className="num">Cycles</th>
                        <th scope="col" className="num">
                          ≥ {data.thresholdMinutes}m
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byStore.map((row) => (
                        <tr
                          key={row.locationId}
                          className={row.locationId === worstLocationId ? "is-flagged" : undefined}
                        >
                          <td className="cell-title">
                            <div className="sq-strong">{row.name}</div>
                            {row.locationPin ? (
                              <div className="sq-muted">{row.locationPin}</div>
                            ) : null}
                          </td>
                          <td className="num sq-strong" data-label="Avg">
                            {formatMinutes(row.avgMinutes)}
                          </td>
                          <td className="num" data-label="Longest">
                            {formatMinutes(row.maxMinutes)}
                          </td>
                          <td className="num" data-label="Cycles">
                            {row.completeEvents}
                          </td>
                          <td className="num" data-label={`≥ ${data.thresholdMinutes}m`}>
                            {row.overThresholdCount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card
              flush
              title="Longest-sitting machines"
              subtitle="Individual worst occurrences in this range."
              aria-label="Worst offenders"
            >
              {data.worstOffenders.length === 0 ? (
                <EmptyState compact title="None recorded" />
              ) : (
                <div className="sq-table-wrap">
                  <table className="sq-table is-stacked">
                    <thead>
                      <tr>
                        <th scope="col">When</th>
                        <th scope="col">Store / machine</th>
                        <th scope="col" className="num">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.worstOffenders.map((row, idx) => (
                        <tr key={`${row.locationId}-${row.machine}-${idx}`}>
                          <td data-label="When" className="nowrap">
                            {moment(row.occurredAt).format("DD/MM HH:mm")}
                          </td>
                          <td className="cell-title">
                            <div className="sq-strong">{row.name}</div>
                            <div className="sq-muted">
                              {row.locationPin ? `${row.locationPin} · ` : ""}
                              {row.machine}
                            </div>
                          </td>
                          <td className="num sq-strong" data-label="Time">
                            {formatMinutes(row.minutes)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        ) : null}
      </div>
    </PageContainer>
  );
}
